<template>
    <div class="ColorsExampleContainer">
        <section class="tprmThemeColors">
            <h2 class="tprmThemeColors__title">
                Tprm Theme colors
            </h2>
            <p class="tprmThemeColors__desc">
                Click a swatch to copy the CSS variable (e.g. <code>var(--tprm-bg)</code>), or click the variable name or hex value below to copy that specific format.
            </p>
            <div
                v-for="theme in tprmColorThemes"
                :key="theme.name"
                class="tprmThemeColors__theme"
            >
                <div class="tprmThemeColors__grid">
                    <div
                        v-for="variable in theme.variables"
                        :key="variable"
                        class="tprmThemeColors__swatch-wrapper"
                    >
                        <button
                            type="button"
                            class="tprmThemeColors__swatch"
                            :style="{ background: `var(${variable})` }"
                            :title="`Click to copy var(${variable})`"
                            @click="copyVariable(variable)"
                        >
                            <span v-if="copiedVariable === variable" class="tprmThemeColors__swatch-feedback">
                                Copied!
                            </span>
                        </button>
                        <div class="tprmThemeColors__swatch-caption">
                            <div class="tprmThemeColors__caption-button-wrapper">
                                <button
                                    type="button"
                                    class="tprmThemeColors__caption-button"
                                    :class="{ 'tprmThemeColors__caption-button--copied': copiedItem === `${variable}-var` }"
                                    @click="copyVariable(variable)"
                                >
                                    {{ copiedItem === `${variable}-var` ? 'Copied!' : variable }}
                                </button>
                                <span
                                    v-if="getSourceVariable(variable)"
                                    class="tprmThemeColors__tooltip"
                                >
                                    {{ getSourceVariable(variable) }}
                                </span>
                            </div>
                            <button
                                type="button"
                                class="tprmThemeColors__caption-button tprmThemeColors__caption-button--hex"
                                :class="{ 'tprmThemeColors__caption-button--copied': copiedItem === `${variable}-hex` }"
                                @click="copyHexValue(variable)"
                            >
                                {{ copiedItem === `${variable}-hex` ? 'Copied!' : getHexValue(variable) }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section class="ColorsExample">
            <h2 class="ColorsExample__title">
                Evident colors
            </h2>
            <p class="ColorsExample__desc">
                Click a swatch to copy the CSS variable (e.g. <code>var(--eidGreen)</code>), or click the variable name or hex value below to copy that specific format.
            </p>
            <div
                v-for="theme in colorThemes"
                :key="theme.name"
                class="ColorsExample__theme"
            >
                <div class="ColorsExample__grid">
                    <div
                        v-for="variable in theme.variables"
                        :key="variable"
                        class="ColorsExample__swatch-wrapper"
                    >
                        <button
                            type="button"
                            class="ColorsExample__swatch"
                            :style="{ background: `var(${variable})` }"
                            :title="`Click to copy var(${variable})`"
                            @click="copyVariable(variable)"
                        >
                            <span v-if="copiedVariable === variable" class="ColorsExample__swatch-feedback">
                                Copied!
                            </span>
                        </button>
                        <div class="ColorsExample__swatch-caption">
                            <button
                                type="button"
                                class="ColorsExample__caption-button"
                                :class="{ 'ColorsExample__caption-button--copied': copiedItem === `${variable}-var` }"
                                @click="copyVariable(variable)"
                            >
                                {{ copiedItem === `${variable}-var` ? 'Copied!' : variable }}
                            </button>
                            <button
                                type="button"
                                class="ColorsExample__caption-button ColorsExample__caption-button--hex"
                                :class="{ 'ColorsExample__caption-button--copied': copiedItem === `${variable}-hex` }"
                                @click="copyHexValue(variable)"
                            >
                                {{ copiedItem === `${variable}-hex` ? 'Copied!' : getHexValue(variable) }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.ColorsExampleContainer {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.ColorsExample, .tprmThemeColors {
    padding: 1.5rem;
    max-width: 56rem;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &__title {
        margin: 0 0 0.25rem;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--eidTechGray);
    }

    &__desc {
        margin: 0 0 1.25rem;
        font-size: 0.8125rem;
        color: var(--eidTechGray-75);

        code {
            padding: 0.125rem 0.25rem;
            font-size: 0.75rem;
            background: var(--eidTechGray-10);
            border-radius: 0.25rem;
        }
    }

    &__theme {
        margin-bottom: 1.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
        gap: 0.75rem;
    }

    &__swatch-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__swatch {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 5rem;
        padding: 0.5rem;
        border: 1px solid var(--eidTechGray-25);
        border-radius: 0.375rem;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        }

        &:focus {
            outline: 2px solid var(--eidTrustGreen);
            outline-offset: 2px;
        }
    }

    &__swatch-feedback {
        position: absolute;
        top: 50%;
        left: 50%;
        font-size: 0.6rem;
        font-weight: 600;
        color: var(--eidQualityBlack);
        background: rgba(255, 255, 255, 0.95);
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        opacity: 0.95;
        transform: translate(-50%, -50%);
        pointer-events: none;
    }

    &__swatch-caption {
        margin-top: 0.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        text-align: center;
    }

    &__caption-button-wrapper {
        position: relative;
        display: inline-block;
    }

    &__caption-button {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        font-size: 0.75rem;
        color: var(--eidQualityBlack);
        text-align: center;
        word-break: break-word;
        line-height: 1.3;
        cursor: pointer;
        transition: color 0.15s ease;

        &:hover {
            color: var(--eidTrustGreen);
        }

        &:focus {
            outline: 2px solid var(--eidTrustGreen);
            outline-offset: 2px;
            border-radius: 0.125rem;
        }
    }

    &__tooltip {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-0.5rem);
        padding: 0.375rem 0.625rem;
        background: var(--eidQualityBlack);
        color: #fff;
        font-size: 0.625rem;
        font-family: monospace;
        white-space: nowrap;
        border-radius: 0.25rem;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease, transform 0.15s ease;
        z-index: 10;
        margin-bottom: 0.25rem;

        &::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 0.25rem solid transparent;
            border-top-color: var(--eidQualityBlack);
        }
    }

    &__caption-button-wrapper:hover &__tooltip {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }

    &__caption-button--hex {
        font-size: 0.6875rem;
        color: var(--eidQualityBlack);
        font-family: monospace;
        font-weight: 500;
    }

    &__caption-button--copied {
        color: var(--eidTrustGreen);
        font-weight: 600;
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { EVIDENT_COLOR_THEMES } from './evidentColorVariables';

    const CSS_VAR_PREFIX = 'var(';

    export default defineComponent({
        name: 'ColorsExample',
        data() {
            return {
                colorThemes: EVIDENT_COLOR_THEMES,
                tprmColorThemes: [
                    {
                        name: 'Background',
                        variables: [
                            '--tprm-base-bg',
                        ],
                    },
                ],
                tprmSourceVariables: {
                    '--tprm-bg': '--eidBgGrayTones-lightest',
                } as Record<string, string>,
                copiedVariable: null as string | null,
                copiedItem: null as string | null,
                copyFeedbackTimeout: null as ReturnType<typeof setTimeout> | null,
                hexCache: {} as Record<string, string>,
            };
        },
        mounted() {
            // Pre-compute hex values for all variables
            this.colorThemes.forEach((theme) => {
                theme.variables.forEach((variable) => {
                    this.getHexValue(variable);
                });
            });
            this.tprmColorThemes.forEach((theme) => {
                theme.variables.forEach((variable) => {
                    this.getHexValue(variable);
                });
            });
        },
        beforeUnmount() {
            if (this.copyFeedbackTimeout) {
                clearTimeout(this.copyFeedbackTimeout);
            }
        },
        methods: {
            getHexValue(variable: string): string {
                // Return cached value if available
                if (this.hexCache[variable]) {
                    return this.hexCache[variable];
                }

                // Get computed background color value
                const tempEl = document.createElement('div');
                tempEl.style.background = `var(${variable})`;
                tempEl.style.position = 'absolute';
                tempEl.style.visibility = 'hidden';
                document.body.appendChild(tempEl);
                const computedColor = window.getComputedStyle(tempEl).backgroundColor;
                document.body.removeChild(tempEl);

                // Convert rgb/rgba to hex
                const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
                if (rgbMatch) {
                    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0');
                    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0');
                    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0');
                    const hex = `#${r}${g}${b}`.toUpperCase();
                    this.hexCache[variable] = hex;
                    return hex;
                }

                // Fallback if conversion fails
                return computedColor;
            },
            copyVariable(variable: string) {
                const value = `${CSS_VAR_PREFIX}${variable})`;
                navigator.clipboard.writeText(value).then(() => {
                    if (this.copyFeedbackTimeout) {
                        clearTimeout(this.copyFeedbackTimeout);
                    }
                    this.copiedVariable = variable;
                    this.copiedItem = `${variable}-var`;
                    this.copyFeedbackTimeout = setTimeout(() => {
                        this.copiedVariable = null;
                        this.copiedItem = null;
                        this.copyFeedbackTimeout = null;
                    }, 2000);
                });
            },
            copyHexValue(variable: string) {
                const hexValue = this.getHexValue(variable);
                navigator.clipboard.writeText(hexValue).then(() => {
                    if (this.copyFeedbackTimeout) {
                        clearTimeout(this.copyFeedbackTimeout);
                    }
                    this.copiedItem = `${variable}-hex`;
                    this.copyFeedbackTimeout = setTimeout(() => {
                        this.copiedItem = null;
                        this.copyFeedbackTimeout = null;
                    }, 2000);
                });
            },
            getSourceVariable(variable: string): string | null {
                return this.tprmSourceVariables[variable] || null;
            },
        },
    });
</script>
